/**
 * Liste „In der Probezeit“ auf der Seite Onboarding-Erfolg (Spezifikation 2026-09-16, R1/R2/R7).
 * Führungskräfte sehen ihre Linie, HR alle. Die Wirtschaftsspalten hängen an den Personen der Liste
 * (`zeigeWirtschaftsspalten` vom Backend), Spalte und Filter „Führungskraft“ an den Führungskräften der
 * ungefilterten Liste (`fuehrungskraftAnzeige`): beides nicht an der Rolle.
 *
 * Die Liste hat ihre eigene Abfrage und hängt an keiner anderen der Seite. Sie steht in jedem Zustand
 * der Seite da.
 *
 * Jede Zahl und jeder Satz kommt aus `lib/probezeit-anzeige.ts`: welche Spalten es gibt (`listenSpalten`),
 * was in jeder Zelle steht, Rundung, Zahlenform, Pille, Datum, Hervorhebung des Endes, Bindung, die Sätze für
 * Laden, Fehler und leere Liste. Hier stehen die Anordnung, die Breiten und die Farbbedingungen.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ReactElement, type ReactNode, useState } from "react";
import { useTabellenSortierung } from "../../hooks/useTabellenSortierung.js";
import {
  type ProbezeitListe as Liste,
  type ProbezeitAmpel,
  type ProbezeitZeile,
  probezeitApi,
} from "../../lib/api.js";
import {
  AMPEL_FARBE,
  AMPEL_TEXTFARBE,
  KEIN_WERT,
  type ListenSpalte,
  type SpaltenKopf,
  type UmsatzZelle,
  type WertZelle,
  bindungAnzeige,
  endeAnzeige,
  fuehrungskraftAnzeige,
  fuehrungskraftText,
  heuteIso,
  krankheitZelle,
  listenHinweisText,
  listenSpalten,
  probezeitSortSpalten,
  probezeitStandardReihenfolge,
  standListeText,
  stundenZelle,
  umsatzZelle,
  untertitelText,
  vkZelle,
  wocheZelle,
} from "../../lib/probezeit-anzeige.js";
import { KpiInfo } from "../ui/KpiInfo.js";
import { SortKopf } from "../ui/SortKopf.js";

/** Die Sortierwerte hängen nicht vom Zustand ab. Ein fester Wert hält den Hook stabil. */
const SORTIERUNG = probezeitSortSpalten();

/** Die Schnittstelle rechnet bei jeder Anfrage die ganze Belegschaft. Fünf Minuten wie die Seite selbst. */
const FRISCH_BIS = 5 * 60_000;

const GEDAEMPFT = { color: "var(--text-muted)" };
const HAUPTTEXT = { color: "var(--text-primary)" };

/** Ein fehlender Wert steht blass da, jeder andere in der Textfarbe. */
const stilVon = (text: string) => (text === KEIN_WERT ? GEDAEMPFT : HAUPTTEXT);

/** Abstand zwischen den Spalten (px) und seitlicher Rand der Zeile (rem, `px-4`). */
const SPALTENABSTAND_PX = 12;
const ZEILENRAND_REM = 2;

/**
 * Breite je Spalte. Die Breiten sind gemessen: das jeweils breiteste Stück Text der Spalte (Kopf samt
 * Info-Symbol oder Zelle) ohne Umbruch, mit etwas Luft. Bei „Eigene Krankheitstage“ und „Probezeit endet“
 * bestimmt der Kopf die Breite, damit er nicht abgeschnitten wird. Mitarbeiter und Führungskraft dehnen
 * sich, der Rest ist fest, damit Kopf und Zeilen in jeder Fensterbreite untereinander stehen.
 */
const BREITE: Record<ListenSpalte, { rem: number; dehnbar?: number }> = {
  name: { rem: 9.5, dehnbar: 2 },
  fuehrungskraft: { rem: 7, dehnbar: 1 },
  woche: { rem: 3.75 },
  umsatz: { rem: 12 },
  stunden: { rem: 4.25 },
  vk: { rem: 4.5 },
  krankheit: { rem: 8.75 },
  ende: { rem: 6.75 },
  bindung: { rem: 5.5 }, // „Kündigung bestätigt“ bricht um
};

/**
 * Spaltenraster von Kopf und Zeilen, aus denselben Spalten. Beide benutzen dieselbe Vorlage, sonst laufen
 * sie auseinander. `mindestBreite` hält die Zeilen zusammen, wenn das Fenster schmaler ist als die
 * Spalten: Die Liste scrollt dann seitlich, statt dass die letzten Spalten abgeschnitten werden.
 */
function rasterVon(spalten: readonly SpaltenKopf[]) {
  const breiten = spalten.map((s) => BREITE[s.spalte]);
  const summeRem = breiten.reduce((summe, b) => summe + b.rem, 0);
  const abstaende = (breiten.length - 1) * SPALTENABSTAND_PX;
  return {
    vorlage: breiten
      .map((b) => (b.dehnbar ? `minmax(${b.rem}rem, ${b.dehnbar}fr)` : `${b.rem}rem`))
      .join(" "),
    mindestBreite: `calc(${summeRem + ZEILENRAND_REM}rem + ${abstaende}px)`,
  };
}

export function ProbezeitListe({ onPerson }: { onPerson: (personioId: number) => void }) {
  const [wahl, setWahl] = useState<number | null>(null);

  // Spalte und Filter „Führungskraft“ und die Auswahl kommen immer aus der ungefilterten Liste. Aus der
  // gefilterten bliebe nach dem ersten Klick genau eine Führungskraft übrig, die Auswahl verschwände und
  // man käme von dieser Führungskraft nicht mehr weg. Ohne Filter ist es dieselbe Anfrage wie unten (ein Abruf).
  //
  // Keine Wiederholung bei einem Fehler (`retry: false`, beide Abfragen): Jede Anfrage rechnet auf dem
  // Server die ganze Belegschaft, mit der Voreinstellung der Anwendung kostete eine fehlschlagende Liste
  // drei dieser Rechnungen. `useProbezeitPerson` schaltet sie aus demselben Grund aus.
  const ungefiltert = useQuery<Liste>({
    queryKey: ["probezeit-liste", null],
    queryFn: () => probezeitApi.getListe(),
    staleTime: FRISCH_BIS,
    retry: false,
  });
  const fk = fuehrungskraftAnzeige(ungefiltert.data?.zeilen ?? [], wahl);

  const liste = useQuery<Liste>({
    queryKey: ["probezeit-liste", fk.gewaehlt],
    queryFn: () => probezeitApi.getListe(fk.gewaehlt ?? undefined),
    staleTime: FRISCH_BIS,
    retry: false,
    // Beim Wechsel der Führungskraft bleibt die alte Liste stehen, bis die neue da ist. Sonst
    // verschwände mit jedem Wechsel die ganze Liste samt Auswahl für einen Augenblick.
    placeholderData: keepPreviousData,
  });

  const daten = liste.data;
  let inhalt: ReactNode;
  if (liste.isLoading) {
    inhalt = <Hinweis>{listenHinweisText("laedt")}</Hinweis>;
  } else if (liste.isError || !daten) {
    inhalt = <Hinweis farbe="var(--risk-high-text)">{listenHinweisText("fehler")}</Hinweis>;
  } else if (daten.zeilen.length === 0) {
    inhalt = <Hinweis>{listenHinweisText("leer")}</Hinweis>;
  } else {
    // Neuer Filter, neue Sortierung: Mit dem Schlüssel beginnt die Tabelle in der Standardreihenfolge.
    // Sonst bliebe eine Sortierung nach einer Spalte stehen, die es für die andere Führungskraft
    // nicht gibt (die Wirtschaftsspalten hängen an den Personen der gefilterten Liste).
    inhalt = (
      <Tabelle
        key={fk.gewaehlt ?? "alle"}
        daten={daten}
        spalten={listenSpalten({
          fuehrungskraft: fk.spalte,
          wirtschaft: daten.zeigeWirtschaftsspalten,
        })}
        onPerson={onPerson}
        wirdAktualisiert={liste.isPlaceholderData}
      />
    );
  }

  return (
    <div className="card overflow-x-auto shrink-0">
      {fk.filter && (
        <div className="px-4 py-2" style={{ borderBottom: "1px solid var(--border)" }}>
          <select
            aria-label="Nach Führungskraft filtern"
            value={fk.gewaehlt ?? ""}
            onChange={(e) => setWahl(e.target.value === "" ? null : Number(e.target.value))}
            className="px-3 py-1.5 text-xs rounded"
            style={{
              background: "var(--bg-surface)",
              color: "var(--text-primary)",
              border: "1px solid var(--border)",
            }}
          >
            <option value="">Alle Führungskräfte</option>
            {fk.auswahl.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}
      {inhalt}
    </div>
  );
}

/** Ein Satz im Kasten der Liste: Laden, Fehler, leere Liste. */
function Hinweis({ children, farbe }: { children: ReactNode; farbe?: string }) {
  return (
    <p className="px-4 py-3 text-sm" style={{ color: farbe ?? "var(--text-muted)" }}>
      {children}
    </p>
  );
}

function Tabelle({
  daten,
  spalten,
  onPerson,
  wirdAktualisiert,
}: {
  daten: Liste;
  spalten: readonly SpaltenKopf[];
  onPerson: (personioId: number) => void;
  wirdAktualisiert: boolean;
}) {
  const { klick, richtungVon, sortiere } = useTabellenSortierung<ProbezeitZeile>(
    SORTIERUNG,
    probezeitStandardReihenfolge,
  );
  const { vorlage, mindestBreite } = rasterVon(spalten);
  const heute = heuteIso();

  return (
    <div
      aria-busy={wirdAktualisiert}
      style={{ minWidth: mindestBreite, opacity: wirdAktualisiert ? 0.6 : 1 }}
    >
      <div
        className="grid text-xs font-medium px-4 py-3"
        style={{
          gridTemplateColumns: vorlage,
          gap: `0 ${SPALTENABSTAND_PX}px`,
          ...GEDAEMPFT,
          borderBottom: "1px solid var(--border)",
        }}
      >
        {spalten.map((spalte) => (
          <SortKopf
            key={spalte.spalte}
            label={spalte.label}
            spalte={spalte.spalte}
            zusatz={spalte.zusatz}
            info={spalte.info}
            rechts={spalte.rechts}
            richtung={richtungVon(spalte.spalte)}
            onKlick={klick}
          />
        ))}
      </div>

      {sortiere(daten.zeilen).map((zeile) => (
        <Zeile
          key={zeile.personioId}
          zeile={zeile}
          spalten={spalten}
          vorlage={vorlage}
          heute={heute}
          onPerson={onPerson}
        />
      ))}

      {/* „Letzter Abgleich“ gehört zu Stunden und Umsatz, deshalb nur mit den Wirtschaftsspalten. */}
      {daten.zeigeWirtschaftsspalten && daten.stand && (
        <p className="px-4 py-2 text-[11px] flex items-center gap-1" style={GEDAEMPFT}>
          {standListeText(daten.stand)}
          <KpiInfo schluessel="probezeitWoche" label="Woche" />
        </p>
      )}
    </div>
  );
}

function Zeile({
  zeile,
  spalten,
  vorlage,
  heute,
  onPerson,
}: {
  zeile: ProbezeitZeile;
  spalten: readonly SpaltenKopf[];
  vorlage: string;
  heute: string;
  onPerson: (personioId: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPerson(zeile.personioId)}
      className="grid w-full text-left px-4 py-3 text-sm row-hover"
      style={{
        gridTemplateColumns: vorlage,
        gap: `0 ${SPALTENABSTAND_PX}px`,
        borderBottom: "1px solid var(--border)",
        // Künftige Eintritte stehen grau in der Liste (R1)
        opacity: zeile.lage === "kuenftig" ? 0.6 : 1,
      }}
    >
      {spalten.map((spalte) => (
        <Zelle key={spalte.spalte} spalte={spalte.spalte} zeile={zeile} heute={heute} />
      ))}
    </button>
  );
}

/**
 * Eine Zelle der Zeile. Der Rückgabetyp lässt `undefined` nicht zu: Kommt zu `ListenSpalte` eine Spalte
 * dazu, meldet `tsc` den fehlenden Fall, statt dass die Zelle still fehlt.
 */
function Zelle({
  spalte,
  zeile,
  heute,
}: {
  spalte: ListenSpalte;
  zeile: ProbezeitZeile;
  heute: string;
}): ReactElement {
  switch (spalte) {
    case "name": {
      const untertitel = untertitelText(zeile);
      return (
        <span className="min-w-0">
          <span className="block truncate" style={HAUPTTEXT} title={zeile.name}>
            {zeile.name}
          </span>
          {/* Eine Zeile, bei langen Teamnamen mit Auslassungspunkten: Der volle Text steht im Tooltip. */}
          {untertitel && (
            <span
              className="block truncate text-[11px] leading-4"
              style={GEDAEMPFT}
              title={untertitel}
            >
              {untertitel}
            </span>
          )}
        </span>
      );
    }
    case "fuehrungskraft":
      return (
        <span
          className="truncate"
          style={GEDAEMPFT}
          title={zeile.fuehrungskraft?.name ?? undefined}
        >
          {fuehrungskraftText(zeile.fuehrungskraft)}
        </span>
      );
    case "woche": {
      const woche = wocheZelle(zeile.wirtschaft);
      return (
        <span className="text-right tabular-nums" style={stilVon(woche)}>
          {woche}
        </span>
      );
    }
    case "umsatz":
      return <UmsatzSpalte zelle={umsatzZelle(zeile.wirtschaft)} />;
    case "stunden":
      return <WertSpalte zelle={stundenZelle(zeile.wirtschaft)} />;
    case "vk":
      return <WertSpalte zelle={vkZelle(zeile.wirtschaft)} />;
    case "krankheit": {
      const krankheit = krankheitZelle(zeile.krankheit);
      return (
        <span className="text-right tabular-nums" style={stilVon(krankheit.tage)}>
          {krankheit.tage}
          {krankheit.meldungen && (
            <span className="block text-[11px] leading-4" style={GEDAEMPFT}>
              {krankheit.meldungen}
            </span>
          )}
        </span>
      );
    }
    case "ende": {
      const ende = endeAnzeige(zeile, heute);
      return (
        <span>
          {/* Die letzten 30 Tage der Probezeit sind hervorgehoben (Erklärtext zu „Probezeit endet“).
              Farbe und Fettschrift: Die Farbe allein sähe nicht jeder. */}
          <span
            className={ende.bald ? "font-semibold" : undefined}
            style={{ color: ende.bald ? "var(--risk-medium-text)" : "var(--text-primary)" }}
          >
            {ende.datum}
          </span>
          <span className="block text-[11px] leading-4" style={GEDAEMPFT}>
            {ende.zusatz}
          </span>
        </span>
      );
    }
    case "bindung": {
      const bindung = bindungAnzeige(zeile);
      return (
        <span style={{ color: bindung.farbe ?? "var(--text-secondary)" }}>{bindung.text}</span>
      );
    }
  }
}

/** Spalte „Umsatz gegen Ziel“: die Pille mit Umsatz und Ziel darunter, oder ein blasser Text. */
function UmsatzSpalte({ zelle }: { zelle: UmsatzZelle }) {
  if (zelle.art === "pille") {
    return (
      <span className="text-right">
        <AmpelPille ampel={zelle.ampel} text={zelle.pille} />
        <span className="block text-[11px] leading-4 mt-0.5" style={GEDAEMPFT}>
          {zelle.zeile}
        </span>
      </span>
    );
  }
  return (
    <span className="text-right" style={GEDAEMPFT}>
      {zelle.text}
      {zelle.zeile && <span className="block text-[11px] leading-4">{zelle.zeile}</span>}
    </span>
  );
}

/** Spalten „Stunden“ und „Ø VK“: der Wert und darunter das Ziel. */
function WertSpalte({ zelle }: { zelle: WertZelle }) {
  return (
    <span className="text-right tabular-nums" style={stilVon(zelle.wert)}>
      {zelle.wert}
      {zelle.ziel && (
        <span className="block text-[11px] leading-4" style={GEDAEMPFT}>
          {zelle.ziel}
        </span>
      )}
    </span>
  );
}

function AmpelPille({ ampel, text }: { ampel: ProbezeitAmpel; text: string }) {
  const flaeche = AMPEL_FARBE[ampel];
  return (
    <span
      className="inline-block max-w-full px-2 py-0.5 rounded text-xs font-semibold"
      style={{
        // Fläche in der Füllfarbe, Schrift in der Textfarbe (Kontrast, siehe AMPEL_TEXTFARBE).
        // Ohne Ampel (Woche 1 bis 3) steht auch die Quote in der Pille: --text-secondary statt
        // --text-muted, damit sie lesbar bleibt.
        background: flaeche ? `color-mix(in srgb, ${flaeche} 15%, transparent)` : "var(--bg-hover)",
        color: AMPEL_TEXTFARBE[ampel] ?? "var(--text-secondary)",
      }}
    >
      {text}
    </span>
  );
}
